import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Optional, ViewChild } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { ProductViewComponent } from '../../../product/view/product-view.component';
import { CuralateGalleryDirective } from './../../../../../module/curalate/curalate-gallery.directive';

export interface IPageComponentCuralateGalleryData extends NICmsContentComponent.IData {
    title?: string;
    copy?: string;
    gallery_id: string;
}

export const PAGE_COMPONENT_CURALATE_GALLERY_ID = 'curalate-gallery';

const config = {
    id: PAGE_COMPONENT_CURALATE_GALLERY_ID,
    name: 'Curalate Gallery',
    tags: ['Homepage'],
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            title: 'Title',
            type: NICmsContentField.EFieldType.text
        },
        <NICmsContentField.IField>{
            key: 'copy',
            title: 'Copy',
            type: NICmsContentField.EFieldType.textarea
        },
        <NICmsContentField.IField>{
            key: 'gallery_id',
            title: 'Gallery Id',
            type: NICmsContentField.EFieldType.text,
            required: true
        }
    ]
};

@Component({
    selector: 'page-component-curalate-gallery',
    templateUrl: './curalate-gallery.html',
    styleUrl: './curalate-gallery.scss',
    standalone: true,
    imports: [CuralateGalleryDirective, ICmsClientModule, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentCuralateGalleryComponent>(config)
export class PageComponentCuralateGalleryComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentCuralateGalleryData;

    filter = '';

    /**
     * Check timeout id
     */
    protected checkTimeoutId;
    /**
     * Max check tries after refreshed
     */
    protected WAITING_MAX = 5;
    /**
     * Check tries count
     */
    protected waiting = 0;

    @ViewChild(CuralateGalleryDirective)
    galleryRef!: CuralateGalleryDirective;

    constructor(@Optional() protected productViewComponent: ProductViewComponent) {
        super();

        // if product view component we subscribe to product change
        if (this.productViewComponent) {
            this.subscriptions.add(
                this.productViewComponent.modelChange.subscribe(() => this.onProductChange(this.productViewComponent.model))
            );

            if (this.productViewComponent.model) {
                this.onProductChange(this.productViewComponent.model);
            }
        }
    }

    /**
     * on loaded callback
     */
    onLoaded(result: boolean): void {
        this.clear();

        if (result) {
            this.check();
        }
    }

    /**
     * Returns if curalate gallery has items
     * @returns {boolean}
     */
    get hasContent(): boolean {
        if (this.galleryRef) {
            return this.galleryRef.hasContent();
        }

        return false;
    }

    /**
     * On product model change callback
     * @param {ProductDecorator<IProductDataV3>} product
     */
    protected onProductChange(product: ProductDecorator<IProductDataV3>): void {
        this.filter = this.getFilter(product);

        this.detectChanges();
    }

    protected getFilter(product: ProductDecorator<IProductDataV3>): string {
        if (product?.data) {
            const sku = product.parent?.isConfigurable ? product.parent.data?.sku : product.data.sku;

            return `productId:${sku}`;
        }

        return '';
    }

    /**
     * Checks if curalate widgets have content
     */
    protected check(): void {
        this.detectChanges();

        if (!this.hasContent && this.waiting < this.WAITING_MAX) {
            this.waiting++;
            this.checkTimeoutId = this.timeoutService.setTimeout(() => this.check(), 200);
        }
    }

    protected clear() {
        this.clearCheckTimeout();
        this.waiting = 0;

        this.detectChanges();
    }

    /**
     * Clears check timeout
     */
    protected clearCheckTimeout(): void {
        if (this.checkTimeoutId) {
            clearTimeout(this.checkTimeoutId);
        }
    }
}
